/**
 * Product-card bullets for each paywall intent. Source: Rutan's
 * `handoffs/rutan/paywalls/Paywall-Intent-Table-2026-09-14.xlsx` (Benefit Library + Intents)
 * and the matching cards in `paywall-variants-all.html` (wording updated 2026-09-25).
 *
 * A recommended card shows up to four lead lines, a divider, then up to four lines below it.
 * The other paid card is a short teaser (Pro) or the Advisor Team delta. The comparison table
 * still reads `planFeatures.ts`; these lines are card copy and can name the same entitlement
 * differently.
 */
import type { ScoProduct } from '@src/graphql-types'
import type { IconType } from 'react-icons'
import {
  LuBotMessageSquare,
  LuCalendarClock,
  LuContact,
  LuEye,
  LuGift,
  LuGlobe,
  LuGraduationCap,
  LuHandshake,
  LuMail,
  LuMessageCircle,
  LuMessagesSquare,
  LuMic,
  LuPresentation,
  LuSearch,
  LuSend,
  LuUserPlus,
  LuVideo,
  LuZap,
} from 'react-icons/lu'

import {
  ADVISOR_TEAM_PRODUCT_ID,
  PAYWALL_INTENTS,
  STARTUPS_PRO_PRODUCT_ID,
  type PaywallFeature,
  type PaywallIntentId,
} from './intent'
import { freeWilMessageAllowance, planTierFor } from './planFeatures'

const PLUS_ALL = 'Plus ALL of Startups.com'
const IN_PRO = 'Everything in Startups Pro'
const TEAM_DELTA_LABEL = 'Everything in Startups Pro, plus'

const BENEFITS = {
  P2: { title: 'Guided Courses + Education', icon: LuGraduationCap },
  P3: { title: 'Global Network of Founders', icon: LuGlobe },
  P4: { title: 'Over $25,000 in Partner Perks', icon: LuGift },
  P5: { title: 'Member Matching & Intros', icon: LuHandshake },
  P6: { title: 'Live Workshops & Peer Groups', icon: LuMic },
  P7: { title: 'Investor Database', icon: LuContact },
  P8: { title: 'Curated Investor Matches', icon: LuSearch },
  P9: { title: 'AI-Powered Email Builder and Pitch Deck Auditor', icon: LuPresentation },
  P10: { title: 'Generated Fundraise Emails', icon: LuMail },
  P11: { title: 'Send 3 Investor Messages per Day', icon: LuSend },
  A1: { title: 'Send 15 Investor Messages Daily', icon: LuSend },
  A2: { title: 'Advisor-Led Sprints', icon: LuZap },
  A3: { title: '1:1 Office Hours with Experts', icon: LuCalendarClock },
  N1: { title: 'Direct message any of our approved advisors', icon: LuMessageCircle },
  N2: { title: 'Invite advisors onto a board seat', icon: LuUserPlus },
  N3: { title: 'Direct message any member', icon: LuMessageCircle },
  N4: { title: 'Direct access to Ed Kang, Ryan Rutan, Steve Gacka and Wil Schroter', icon: LuMessagesSquare },
  N5: { title: 'Full slide-by-slide deck audit with actual rewrites', icon: LuPresentation },
  N7: { title: 'AI Investor Matching and Outreach', icon: LuBotMessageSquare },
  N8: { title: 'Investor Database with AI Matching and Outreach', icon: LuContact },
  N9: { title: 'Pitch Deck Audit', icon: LuPresentation },
  N10: { title: 'Pitch Deck Workshops', icon: LuGraduationCap },
  N11: { title: 'Funding Workshops', icon: LuGlobe },
} as const satisfies Record<string, { title: string; icon: IconType }>

type BenefitRef = keyof typeof BENEFITS

type CardComposition = {
  lead: BenefitRef[]
  separator: string
  below: BenefitRef[]
}

const proCard = (lead: BenefitRef[], below: BenefitRef[]): CardComposition => ({
  lead,
  separator: PLUS_ALL,
  below,
})

const teamCard = (lead: BenefitRef[], below: BenefitRef[]): CardComposition => ({
  lead,
  separator: IN_PRO,
  below,
})

/**
 * Recommended-card composition, keyed by the intent that owns it.
 * Rutan's "Publish Requests" lines (P1, N6) were dropped while GroupRequest publishing is off;
 * where a lead lost a line, the first line below the divider moved up.
 */
const CARDS: Partial<Record<PaywallIntentId, CardComposition>> = {
  default: proCard(['P2', 'P3', 'P4', 'N9'], ['P5', 'P6', 'N8']),
  'advisor-message': proCard(['N1', 'N2', 'P5', 'N7'], ['P2', 'P4', 'P6']),
  'advisor-invite': proCard(['N2', 'N1', 'P5', 'P2'], ['P4', 'P6']),
  'member-match': proCard(['P5', 'N3', 'P3', 'P6'], ['P2', 'P4']),
  'perk-unlock': proCard(['P4', 'P3', 'P2', 'P6'], ['P5', 'N1']),
  'investor-search': proCard(['P7', 'P8', 'P10', 'P11'], ['P9', 'P3']),
  'deck-slide-lock': proCard(['N5', 'P2', 'P7', 'P8'], ['N7', 'P10', 'P3']),
  'content-lock': proCard(['P2', 'P6', 'P3', 'P4'], ['P5', 'N1']),
  'event-rsvp': proCard(['P6', 'P3', 'P5', 'P2'], ['P4', 'N1']),
  'goal:customers': proCard(['P5', 'P3', 'P6', 'P2'], ['P4', 'N1']),
  'goal:customers.first-customers': proCard(['P5', 'P3', 'P6', 'N7'], ['P2', 'P4', 'N1']),
  'goal:customers.scale-sales': proCard(['P5', 'P6', 'P2', 'P3'], ['P4', 'N1']),
  'goal:funding': proCard(['P7', 'P8', 'P10', 'N5'], ['P3', 'P2']),
  'goal:funding.friends-family': proCard(['P2', 'P6', 'N5', 'P3'], ['P4', 'P5']),
  'goal:funding.angel': proCard(['P7', 'P8', 'P10', 'N5'], ['P3', 'P9']),
  'goal:funding.vc': teamCard(['N5', 'A3', 'A2', 'A1'], ['N10', 'N11', 'P7', 'P8']),
  'goal:cofounder': proCard(['P5', 'N3', 'P3', 'P6'], ['P2', 'P4']),
  'goal:mvp': proCard(['P4', 'P5', 'P2', 'P3'], ['P6', 'N1']),
  'leadership-message': teamCard(['N4', 'A3', 'A2', 'A1'], ['P7', 'N9', 'P5', 'P2']),
  'office-hours': teamCard(['A3', 'A2', 'N4', 'A1'], ['P2', 'P5', 'P7']),
  'premium-event': teamCard(['A3', 'A2', 'N4'], ['P6', 'P3', 'P5']),
}

/**
 * Intents added after the handoff. They reuse the nearest Rutan card so the wording stays
 * in the same library. `wil-limit` is not here: that wall still leads with unlimited Wil.ai.
 */
const CARD_ALIAS: Partial<Record<PaywallIntentId, PaywallIntentId>> = {
  'member-message': 'member-match',
  'discussion-join': 'event-rsvp',
  'member-directory': 'member-match',
  'customers-write': 'goal:customers',
  'board-apply': 'advisor-invite',
  'fast-send': 'advisor-invite',
  'investor-outreach': 'investor-search',
}

const TEAM_DELTA: BenefitRef[] = ['N4', 'A3', 'A2']
const PRO_TEASER: BenefitRef[] = ['N1', 'P5', 'P7']

export type CardFeatureGroup = {
  /** Heading above the group. Used for the Advisor Team delta. */
  label?: string
  /** Divider rendered above this group's lines. */
  separator?: string
  features: PaywallFeature[]
}

function benefitLines(refs: readonly BenefitRef[]): PaywallFeature[] {
  return refs.map(ref => {
    const benefit = BENEFITS[ref]
    return { key: ref, title: benefit.title, icon: benefit.icon }
  })
}

function freeCardFeatures(): PaywallFeature[] {
  const messages = freeWilMessageAllowance()
  return [
    { key: 'browse-everything', title: 'Browse everything, unlimited', icon: LuEye },
    { key: 'wil-chat', title: `${messages} messages to Wil.ai`, icon: LuBotMessageSquare },
    {
      key: 'paid-sessions',
      title: 'Book and pay for on-demand advisor sessions',
      icon: LuVideo,
    },
  ]
}

function compositionFor(intentId: PaywallIntentId): CardComposition | null {
  const id = CARD_ALIAS[intentId] ?? intentId
  return CARDS[id] ?? null
}

/**
 * The lines a product card shows for this intent. Null when the product is not on the
 * membership ladder, or the intent has no Rutan card (the caller keeps the catalog list).
 * Free is the same three lines on every intent.
 */
export function cardFeatureGroups(product: ScoProduct, intentId: PaywallIntentId): CardFeatureGroup[] | null {
  const tier = planTierFor(product.productId)
  if (tier === 'free') {
    return [{ features: freeCardFeatures() }]
  }
  const composition = compositionFor(intentId)
  if (!tier || !composition) {
    return null
  }
  const recommendedId = PAYWALL_INTENTS[intentId].recommendedProductId
  const recommended = product.productId === recommendedId
  if (!recommended && tier === 'team' && recommendedId === STARTUPS_PRO_PRODUCT_ID) {
    return [{ label: TEAM_DELTA_LABEL, features: benefitLines(TEAM_DELTA) }]
  }
  if (!recommended && tier === 'pro' && recommendedId === ADVISOR_TEAM_PRODUCT_ID) {
    return [{ features: benefitLines(PRO_TEASER) }]
  }
  if (!recommended) {
    return null
  }
  const below = benefitLines(composition.below)
  if (below.length === 0) {
    return [{ features: benefitLines(composition.lead) }]
  }
  return [{ features: benefitLines(composition.lead) }, { separator: composition.separator, features: below }]
}
